import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { X } from "lucide-react";
import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useCurrency, useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { usePaymentMethod, useCreatePlanPayment } from "@features/cx/payments/hooks";
import { PaymentMethod } from "@features/cx/shared/types/consumerRequest";
import { CreatePaymentPayload } from "@features/cx/payments/types";
import { useCustomFieldsForm, CustomFieldConfig } from "@shared/utils/customFormMaker";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import { toast } from "@shared/hooks/use-toast";

// TypeScript Interfaces
export interface PaymentData {
  paymentMode: string;
  amount: number;
  [key: string]: any; // For dynamic custom fields
}

export interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onPaymentComplete: (data: PaymentData) => void | Promise<void>;
  totalDue: number;
  cashReceived?: number;
  outstandingBalance?: number;
  defaultAmount?: number;
  loading?: boolean;
  consumerId: number;
  consumerNo: string;
  planParameters?: any[];
}

export const PaymentDialog: React.FC<PaymentDialogProps> = ({
  open,
  onClose,
  onPaymentComplete,
  totalDue,
  cashReceived = 0,
  outstandingBalance,
  defaultAmount,
  loading = false,
  consumerId,
  consumerNo,
  planParameters = [],
}) => {
  const currency = useCurrency();
  const remoteUtilityId = Number(useRemoteUtilityId());

  // Fetch payment methods from API
  const paymentMethodQuery = usePaymentMethod({
    remoteUtilityId: remoteUtilityId || 0,
  });

  // Payment mutation hook
  const createPaymentMutation = useCreatePlanPayment(
    remoteUtilityId || 0,
    consumerId || 0,
    consumerNo || ""
  );

  // Get payment methods data
  const paymentMethods = paymentMethodQuery.data?.result
    ? Array.isArray(paymentMethodQuery.data.result)
      ? paymentMethodQuery.data.result
      : [paymentMethodQuery.data.result]
    : [];

  // Convert to options format
  const paymentModeOptions = useMemo(() => {
    return paymentMethods.map((method: PaymentMethod) => ({
      value: method.code,
      label: method.name,
    }));
  }, [paymentMethods]);

  // Main form
  const form = useForm({
    defaultValues: {
      paymentMode: paymentModeOptions[0]?.value || "",
      amount: defaultAmount ,
    },
  });

  // Custom fields form
  const customFieldsForm = useForm({
    mode: "onBlur",
  });

  const selectedPaymentMode = form.watch("paymentMode");


  // Find selected payment method object
  const selectedPaymentMethod = useMemo(() => {
    return paymentMethods.find(
      (method: PaymentMethod) => method.code === selectedPaymentMode
    );
  }, [selectedPaymentMode, paymentMethods]);

  // Custom field configuration
  const customFieldConfig = useMemo<CustomFieldConfig>(
    () => ({
      namingStrategy: "key-fallback",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
      gridColumns: 1,
      fullWidth: true,
      enableTooltips: true,
      fieldNameCleaning: true,
    }),
    []
  );

  // Use the utility hook for payment method custom fields
  const {
    formFields: paymentMethodCustomFields,
    defaultValues: paymentMethodDefaults,
    resetFormWithDefaults,
    hasFields: hasPaymentMethodFields,
  } = useCustomFieldsForm(
    Array.isArray(selectedPaymentMethod?.customFields)
      ? selectedPaymentMethod.customFields
      : undefined,
    customFieldsForm,
    customFieldConfig
  );



  // Reset custom fields form when payment method changes
  useEffect(() => {
    if (
      selectedPaymentMethod &&
      Array.isArray(selectedPaymentMethod.customFields)
    ) {
      resetFormWithDefaults();
    }
  }, [selectedPaymentMethod?.code, resetFormWithDefaults]);
  
    const amountFieldName = useMemo(() => {
  return paymentMethodCustomFields.find(
    (field) =>
      field.name.toLowerCase().includes("amount") ||
      field.label?.toLowerCase().includes("amount") ||
      field.type === "decimal"
  )?.name;
}, [paymentMethodCustomFields]);
    useEffect(() => {
  if (amountFieldName && totalDue) {
    customFieldsForm.setValue(amountFieldName, totalDue.toString());
  }
}, [amountFieldName, totalDue, customFieldsForm]);

const currentAmount = amountFieldName
  ? Number(customFieldsForm.watch("payment_amount") || 0)
  : 0;

  // Build form fields for DynamicForm
  const getFormFields = (): FormField[] => {
    const baseFields: FormField[] = [
      {
        name: "paymentMode",
        label: "Payment Mode",
        type: "select",
        required: true,
        options: paymentModeOptions,
        defaultValue: paymentModeOptions[0]?.value || "",
        classes: FIELD_CLASSES,
      },
    ];

    return baseFields;
  };

  const formFields = useMemo(() => {
    return getFormFields();
  }, [paymentModeOptions, defaultAmount, totalDue]);

  // Calculate outstanding balance
const calculatedOutstandingBalance = useMemo(() => {
  return totalDue - currentAmount;
}, [totalDue, currentAmount]);

const getChangeAmount = () => Math.max(0, currentAmount - totalDue);

  // Credit note state
  const [createCreditNote, setCreateCreditNote] = React.useState<boolean>(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      form.reset({
        paymentMode: paymentModeOptions[0]?.value || "",
        amount: defaultAmount || totalDue,
      });
      customFieldsForm.reset();
      setCreateCreditNote(false);
    }
  }, [open, defaultAmount, totalDue, paymentModeOptions]);

  const handleSubmit = async (data: any) => {
    try {
      // Collect custom fields data
      let customFieldsData = {};
      if (hasPaymentMethodFields) {
        const customFieldNames = paymentMethodCustomFields.map(
          (field) => field.name
        );
        customFieldNames.forEach((fieldName) => {
          const value = customFieldsForm.getValues(fieldName);
          if (value !== undefined && value !== "") {
            customFieldsData[fieldName] = value;
          }
        });
      }

      // Prepare payment payload (same structure as ApplicationSuccessMessage)
      const paymentPayload: CreatePaymentPayload = {
        remoteUtilityId: remoteUtilityId,
        consumer: consumerId,
        amount: totalDue,
        is_plan_payment:true,
        payment_plan_mode: "pay_now",
        paymentPayType: 4, // Registration payment type
        paymentMode: data.paymentMode,
        paymentChannel: "walk-in", // Same as registration
        additional_data:{
        is_plan_payment_done:true,
        plan_parameters: planParameters
    },
        paymentDate: new Date().toISOString(),
        status: "CREDIT",
        source: 1,
        paymentReceivedStatus: 0,
        createCreditNote: createCreditNote,
        extraData: {
          additionalNotes: "Registration payment",
          billAmount: totalDue,
          paymentAmount: Number(data.amount) || 0,
          excessRefund: getChangeAmount(),
          ...customFieldsData,
        },
      };

      console.log("Payment Payload:", paymentPayload);

      // Create payment using the mutation
      await createPaymentMutation.mutateAsync(paymentPayload);

      // Show success toast
      toast({
        title: "Payment Completed",
        description: "Your payment has been processed successfully.",
      });

      // Merge base data with custom fields for callback
      const paymentData: PaymentData = {
        ...data,
        ...customFieldsData,
      };

      // Call the success callback
      await onPaymentComplete(paymentData);

      // Reset and close
      form.reset();
      customFieldsForm.reset();
      setCreateCreditNote(false);
      onClose();
    } catch (error) {
      console.error("Payment submission error:", error);
      toast({
        title: "Payment Failed",
        description: "Failed to process payment. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    form.reset();
    customFieldsForm.reset();
    setCreateCreditNote(false);
    onClose();
  };

  const getPaymentSummary = () => {
    const modeLabel =
      paymentModeOptions.find((m) => m.value === selectedPaymentMode)?.label ||
      selectedPaymentMode;

    return {
      paymentMethodValue: modeLabel,
    };
  };

  const paymentSummary = getPaymentSummary();

    useEffect(() => {
  if (amountFieldName && totalDue && selectedPaymentMethod?.name === "Cash") {
    customFieldsForm.setValue(amountFieldName, totalDue.toString());
  }
}, [amountFieldName, totalDue, customFieldsForm, selectedPaymentMethod?.name, open]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl p-0 gap-0">
        {/* Header */}
        <DialogHeader className="p-6 pb-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-semibold text-gray-900">
              Complete Payment
            </DialogTitle>
          </div>
        </DialogHeader>

        {/* Scrollable Form Content */}
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Base Form Fields (Payment Mode and Amount) */}
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            loading={loading || createPaymentMutation.isPending}
            config={{
              showSubmitButton: false,
              showResetButton: false,
            }}
            gridColumns={1}
          />

          {/* Custom Fields for Selected Payment Method */}
          {hasPaymentMethodFields && selectedPaymentMethod && (
            <div className="space-y-4 bg-gray-50 p-4 rounded-md border border-gray-200">
              <h4 className="font-medium text-gray-900">
                {selectedPaymentMethod.name} Payment Details
              </h4>
              <div className="grid grid-cols-1 gap-4">
                {paymentMethodCustomFields.map((field) => {
                  let inputType: "text" | "number" | "date" = "text";

                  if (field.type === "decimal") {
                    inputType = "number";
                  } else if (field.type === "datepicker") {
                    inputType = "date";
                  }

                  return (
                    <div key={field.name} className="space-y-2">
                      <Label htmlFor={field.name}>
                        {field.label}
                        {field.required && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </Label>
                      <Input
                        id={field.name}
                        type={inputType}
                        step={field.type === "decimal" ? "0.01" : undefined}
                        value={customFieldsForm.watch(field.name) || ""}
                        onChange={(e) => {
                          customFieldsForm.setValue(field.name, e.target.value);
                        }}
                        placeholder={field.placeholder || ""}
                        disabled={field.disabled}
                        required={field.required}
                      />
                      {field.helperText && (
                        <p className="text-xs text-gray-500">
                          {field.helperText}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Payment Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
            <h4 className="text-sm font-semibold text-blue-900 mb-3">
              Payment Summary
            </h4>

            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700">Total Due</span>
              <span className="text-gray-900 font-semibold">
                {currency} {totalDue}
              </span>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700">Payment Method</span>
              <span className="text-gray-900 font-semibold">
                {paymentSummary.paymentMethodValue}
              </span>
            </div>

            {currentAmount > 0 && (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">Amount Paying</span>
                  <span className="text-gray-900 font-semibold">
                    {currency} {currentAmount}
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm pt-3 border-t border-blue-200">
                  <span className="text-gray-900 font-semibold">
                    {calculatedOutstandingBalance > 0 ? "Outstanding Balance" : "Change to Return"}
                  </span>
                  <span
                    className={`text-lg font-bold ${
                      calculatedOutstandingBalance > 0
                        ? "text-red-600"
                        : "text-green-600"
                    }`}
                  >
                    {currency} {Math.abs(calculatedOutstandingBalance)}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Credit Note Checkbox */}
          {getChangeAmount() > 0 && (
            <div className="bg-green-50 p-4 rounded-md border border-green-200">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="createCreditNote"
                  checked={createCreditNote}
                  onChange={(e) => setCreateCreditNote(e.target.checked)}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="createCreditNote"
                  className="text-sm font-medium text-green-800"
                >
                  Create a credit note for change amount
                </label>
              </div>
              <p className="text-xs text-green-700 mt-1 ml-6">
                A credit note will be created for {currency} {getChangeAmount().toFixed(2)} that can be applied to future bills
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 p-6 pt-4 border-t border-gray-200 bg-gray-50">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading || createPaymentMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={form.handleSubmit(handleSubmit)}
            disabled={loading || paymentMethodQuery.isLoading || createPaymentMutation.isPending}
          >
            {createPaymentMutation.isPending ? "Processing..." : "Complete Payment"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentDialog;